/**
 * Trainova AI — Unified Crash-Free Voice & Speech Synthesis Service
 * Provides consistent persona/learner/coach voice matching across Simulation & Replay,
 * with Chromium GC retention, heartbeat pump (preventing 15s freeze), and watchdog timeout.
 */

// Persistent set of active SpeechSynthesisUtterance objects to prevent Chromium Garbage Collection
const activeUtterances = new Set<SpeechSynthesisUtterance>();
let heartbeatInterval: any = null;
// One watchdog per utterance: a stale cancel() from a previous utterance must not
// clear the watchdog that protects the currently speaking one.
const watchdogTimeouts = new Map<SpeechSynthesisUtterance, any>();
let voicesCache: SpeechSynthesisVoice[] = [];

// Global Playback Coordinator to enforce mutual exclusion across all audio/video cards
let currentPlaybackOwner: { id: string; onStop?: () => void } | null = null;

export function claimPlayback(id: string, onStop?: () => void): void {
  if (currentPlaybackOwner && currentPlaybackOwner.id !== id) {
    if (typeof currentPlaybackOwner.onStop === 'function') {
      try {
        currentPlaybackOwner.onStop();
      } catch {}
    }
    stopAllSpeech();
  }
  currentPlaybackOwner = { id, onStop };
}

export function releasePlayback(id: string): void {
  if (currentPlaybackOwner && currentPlaybackOwner.id === id) {
    currentPlaybackOwner = null;
  }
}

// Assigned Voice Cache to prevent voice from randomly switching across turns
const roleVoiceCache = new Map<string, { voice: SpeechSynthesisVoice | null; pitch: number; rate: number }>();

export function preloadVoices(): void {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
  const load = () => {
    try {
      const v = window.speechSynthesis.getVoices();
      if (v && v.length > 0) {
        voicesCache = v;
        // CRITICAL: Do NOT clear roleVoiceCache here!
        // Chromium triggers onvoiceschanged ~1.5 to 2 minutes into a session when background cloud voices refresh.
        // Clearing roleVoiceCache caused the persona voice to randomly change mid-simulation.
        // Instead, update the existing voice references to keep the voice completely stable.
        roleVoiceCache.forEach((entry) => {
          if (entry.voice) {
            const reMatched = v.find((voice) => voice.voiceURI === entry.voice?.voiceURI || voice.name === entry.voice?.name);
            if (reMatched) {
              entry.voice = reMatched;
            }
          }
        });
      }
    } catch {}
  };
  load();
  if (typeof window.speechSynthesis.addEventListener === 'function') {
    try {
      window.speechSynthesis.addEventListener('voiceschanged', load);
    } catch {}
  }
  if (window.speechSynthesis.onvoiceschanged !== undefined) {
    window.speechSynthesis.onvoiceschanged = load;
  }
}

// Preload immediately on module evaluation
if (typeof window !== 'undefined') {
  preloadVoices();
}

export function getCachedVoices(): SpeechSynthesisVoice[] {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return [];
  if (voicesCache.length === 0) {
    try {
      voicesCache = window.speechSynthesis.getVoices();
    } catch {}
  }
  return voicesCache;
}

export interface CountryPreset {
  code: string;
  name: string;
  flag: string;
  locales: string[];
  /** Canonical cloud TTS locale for this accent (may differ from locales[0]). */
  voiceLocale?: string;
  accentName: string;
  cadenceRate: number;
  cadencePitch: number;
  sampleGreeting: string;
}

export const COUNTRY_PRESETS: CountryPreset[] = [
  {
    code: 'IN',
    name: 'India',
    flag: '🇮🇳',
    locales: ['en-IN', 'hi-IN', 'ta-IN', 'mr-IN'],
    accentName: 'Indian English & Indic Accents',
    cadenceRate: 1.02,
    cadencePitch: 1.0,
    sampleGreeting: 'Namaste. I am reaching out to discuss my account requirements with you today.',
  },
  {
    code: 'US',
    name: 'United States',
    flag: '🇺🇸',
    locales: ['en-US'],
    accentName: 'American English (Standard)',
    cadenceRate: 1.0,
    cadencePitch: 1.0,
    sampleGreeting: 'Hello there. I am checking in to review my account options and get this resolved.',
  },
  {
    code: 'GB',
    name: 'United Kingdom',
    flag: '🇬🇧',
    locales: ['en-GB'],
    accentName: 'British English (RP / London)',
    cadenceRate: 0.94,
    cadencePitch: 0.97,
    sampleGreeting: 'Good day. I would like to review the particulars of my inquiry with you.',
  },
  {
    code: 'AU',
    name: 'Australia',
    flag: '🇦🇺',
    locales: ['en-AU'],
    accentName: 'Australian English',
    cadenceRate: 0.98,
    cadencePitch: 1.04,
    sampleGreeting: "G'day. I am hoping you can assist me with my inquiry today.",
  },
  {
    code: 'CA',
    name: 'Canada',
    flag: '🇨🇦',
    locales: ['en-CA'],
    accentName: 'Canadian English',
    cadenceRate: 0.99,
    cadencePitch: 1.0,
    sampleGreeting: "Hello. I'd like to check in on our account review and go over the details.",
  },
  {
    code: 'SG',
    name: 'Singapore',
    flag: '🇸🇬',
    locales: ['en-SG'],
    accentName: 'Singaporean English',
    cadenceRate: 1.05,
    cadencePitch: 1.03,
    sampleGreeting: 'Hello! Can you help me review my account status and clarify the next steps?',
  },
  {
    code: 'AE',
    name: 'United Arab Emirates',
    flag: '🇦🇪',
    locales: ['en-AE', 'ar-AE', 'ar'],
    accentName: 'Middle Eastern English (UAE/GCC)',
    cadenceRate: 0.93,
    cadencePitch: 0.96,
    sampleGreeting: 'Marhaban. I am calling to discuss our scheduled review and clarify the pending action items.',
  },
  {
    code: 'PH',
    name: 'Philippines',
    flag: '🇵🇭',
    locales: ['en-PH'],
    voiceLocale: 'en-PH',
    accentName: 'Philippine English (ASEAN)',
    cadenceRate: 0.97,
    cadencePitch: 1.03,
    sampleGreeting: 'Mabuhay! I am following up on my account request and looking forward to your assistance.',
  },
  {
    code: 'DE',
    name: 'Germany & Europe',
    flag: '🇩🇪',
    locales: ['en-DE', 'de-DE'],
    // "European English" personas speak English; en-DE is not a cloud TTS locale.
    voiceLocale: 'en-GB',
    accentName: 'European English (Euro-continental)',
    cadenceRate: 0.95,
    cadencePitch: 0.97,
    sampleGreeting: 'Guten Tag. I would like to review the technical details and verify our project timeline.',
  },
  {
    code: 'GLOBAL',
    name: 'Global / Neutral',
    flag: '🌐',
    locales: ['en'],
    // Neutral international baseline; keeps the runtime locale concrete.
    voiceLocale: 'en-IN',
    accentName: 'Global International English',
    cadenceRate: 1.0,
    cadencePitch: 1.0,
    sampleGreeting: 'Hello. I am reaching out to discuss our upcoming milestones and coordinate next steps.',
  },
];

export interface TonePreset {
  id: string;
  name: string;
  icon: string;
  badge: string;
  description: string;
  defaultRate: number;
  defaultPitch: number;
  sampleTemplate: string;
}

export const TONE_PRESETS: TonePreset[] = [
  {
    id: 'empathetic',
    name: 'Empathetic & Cooperative',
    icon: '🤝',
    badge: 'Collaborative',
    description: 'Warm, collaborative, receptive, and polite demeanor.',
    defaultRate: 0.98,
    defaultPitch: 1.02,
    sampleTemplate: 'Thank you for taking the time to assist me. I really appreciate your patience and help in guiding me through this.',
  },
  {
    id: 'frustrated',
    name: 'Frustrated & Escalated',
    icon: '😤',
    badge: 'High Stakes',
    description: 'Impatient, urgent, stressed, demanding prompt resolution.',
    defaultRate: 1.12,
    defaultPitch: 1.06,
    sampleTemplate: 'I have been following up on this for three days now with zero clear answers! I need this resolved right now.',
  },
  {
    id: 'skeptical',
    name: 'Skeptical & Analytical',
    icon: '🧐',
    badge: 'Probing',
    description: 'Detail-oriented, questioning assumptions, asking for specifics.',
    defaultRate: 0.94,
    defaultPitch: 0.96,
    sampleTemplate: 'Before we agree to that, can you break down the exact fee schedule and explain how your calculations were verified?',
  },
  {
    id: 'executive',
    name: 'Executive & Decisive',
    icon: '💼',
    badge: 'C-Suite Direct',
    description: 'Concise, high-level, focused on ROI, timelines, and outcomes.',
    defaultRate: 1.06,
    defaultPitch: 0.94,
    sampleTemplate: 'Let us cut straight to the core deliverables. What is your concrete turnaround time and who owns the escalation path?',
  },
  {
    id: 'anxious',
    name: 'Anxious & Hesitant',
    icon: '😟',
    badge: 'Cautious',
    description: 'Nervous, cautious, seeking reassurance and step-by-step clarity.',
    defaultRate: 0.94,
    defaultPitch: 1.08,
    sampleTemplate: 'I am a bit worried about whether this will impact my account balance. Could you please double-check the steps with me?',
  },
  {
    id: 'compliance',
    name: 'Firm & Compliance-Minded',
    icon: '⚖️',
    badge: 'Governance',
    description: 'Strict, policy-adherent, uncompromising on procedural details.',
    defaultRate: 0.98,
    defaultPitch: 0.98,
    sampleTemplate: 'We have statutory compliance guidelines that must be met. Can you confirm this strictly adheres to our documentation mandate?',
  },
  {
    id: 'curious',
    name: 'Curious & Consultative',
    icon: '💡',
    badge: 'Open-Minded',
    description: 'Inquisitive, engaged, open to advisory guidance and options.',
    defaultRate: 1.02,
    defaultPitch: 1.04,
    sampleTemplate: 'That sounds promising! Can you walk me through the different tiers and which one best fits our growth trajectory?',
  },
  {
    id: 'irate',
    name: 'Irate & Confrontational',
    icon: '😡',
    badge: 'Severe Conflict',
    description: 'Angry, combative, demanding immediate escalation or cancellation.',
    defaultRate: 1.15,
    defaultPitch: 1.10,
    sampleTemplate: 'This is completely unacceptable service! Either connect me with a supervisor immediately or process my full cancellation!',
  },
  {
    id: 'neutral',
    name: 'Pragmatic & Neutral',
    icon: '😐',
    badge: 'Standard',
    description: 'Balanced, pragmatic conversational baseline.',
    defaultRate: 1.0,
    defaultPitch: 1.0,
    sampleTemplate: 'Good afternoon. I am calling to discuss our scheduled review and clarify the pending action items.',
  },
];

export interface OpenAITTSVoice {
  id: string;
  name: string;
  gender: 'female' | 'male' | 'neutral';
  toneDescriptor: string;
  accentPreference: string;
}

export const OPENAI_TTS_VOICES: OpenAITTSVoice[] = [
  { id: 'nova', name: 'Nova', gender: 'female', toneDescriptor: 'Warm, expressive, natural cadence', accentPreference: 'Global / Adaptive' },
  { id: 'shimmer', name: 'Shimmer', gender: 'female', toneDescriptor: 'Bright, energetic, crisp enunciation', accentPreference: 'Modern Clear' },
  { id: 'onyx', name: 'Onyx', gender: 'male', toneDescriptor: 'Deep, authoritative, executive presence', accentPreference: 'Deep Baritone' },
  { id: 'echo', name: 'Echo', gender: 'male', toneDescriptor: 'Smooth, consultative, balanced male', accentPreference: 'Consultative Warm' },
  { id: 'fable', name: 'Fable', gender: 'neutral', toneDescriptor: 'Articulate, nuanced, subtle British cadence', accentPreference: 'Anglo-European' },
  { id: 'alloy', name: 'Alloy', gender: 'neutral', toneDescriptor: 'Versatile, balanced, standard corporate', accentPreference: 'Standard Neutral' },
];

export interface GoogleTTSVoice {
  id: string;
  name: string;
  gender: 'female' | 'male';
  tier: 'Standard' | 'WaveNet' | 'Neural2';
  locale: string;
}

export const GOOGLE_TTS_VOICES: Record<string, GoogleTTSVoice[]> = {
  'en-IN': [
    { id: 'en-IN-Standard-A', name: 'Standard A (Female) — Free', gender: 'female', tier: 'Standard', locale: 'en-IN' },
    { id: 'en-IN-Standard-B', name: 'Standard B (Male) — Free', gender: 'male', tier: 'Standard', locale: 'en-IN' },
    { id: 'en-IN-Standard-C', name: 'Standard C (Male) — Free', gender: 'male', tier: 'Standard', locale: 'en-IN' },
    { id: 'en-IN-Standard-D', name: 'Standard D (Female) — Free', gender: 'female', tier: 'Standard', locale: 'en-IN' },
    { id: 'en-IN-Wavenet-A', name: 'WaveNet A (Female) — HD', gender: 'female', tier: 'WaveNet', locale: 'en-IN' },
    { id: 'en-IN-Wavenet-B', name: 'WaveNet B (Male) — HD', gender: 'male', tier: 'WaveNet', locale: 'en-IN' },
    { id: 'en-IN-Wavenet-C', name: 'WaveNet C (Male) — HD', gender: 'male', tier: 'WaveNet', locale: 'en-IN' },
    { id: 'en-IN-Wavenet-D', name: 'WaveNet D (Female) — HD', gender: 'female', tier: 'WaveNet', locale: 'en-IN' },
    { id: 'en-IN-Neural2-A', name: 'Neural2 A (Female) — Studio', gender: 'female', tier: 'Neural2', locale: 'en-IN' },
    { id: 'en-IN-Neural2-B', name: 'Neural2 B (Male) — Studio', gender: 'male', tier: 'Neural2', locale: 'en-IN' },
    { id: 'en-IN-Neural2-C', name: 'Neural2 C (Male) — Studio', gender: 'male', tier: 'Neural2', locale: 'en-IN' },
    { id: 'en-IN-Neural2-D', name: 'Neural2 D (Female) — Studio', gender: 'female', tier: 'Neural2', locale: 'en-IN' },
  ],
  'hi-IN': [
    { id: 'hi-IN-Standard-A', name: 'Standard A (Female) — Free', gender: 'female', tier: 'Standard', locale: 'hi-IN' },
    { id: 'hi-IN-Standard-B', name: 'Standard B (Male) — Free', gender: 'male', tier: 'Standard', locale: 'hi-IN' },
    { id: 'hi-IN-Standard-C', name: 'Standard C (Male) — Free', gender: 'male', tier: 'Standard', locale: 'hi-IN' },
    { id: 'hi-IN-Standard-D', name: 'Standard D (Female) — Free', gender: 'female', tier: 'Standard', locale: 'hi-IN' },
    { id: 'hi-IN-Wavenet-A', name: 'WaveNet A (Female) — HD', gender: 'female', tier: 'WaveNet', locale: 'hi-IN' },
    { id: 'hi-IN-Wavenet-B', name: 'WaveNet B (Male) — HD', gender: 'male', tier: 'WaveNet', locale: 'hi-IN' },
    { id: 'hi-IN-Neural2-A', name: 'Neural2 A (Female) — Studio', gender: 'female', tier: 'Neural2', locale: 'hi-IN' },
    { id: 'hi-IN-Neural2-B', name: 'Neural2 B (Male) — Studio', gender: 'male', tier: 'Neural2', locale: 'hi-IN' },
  ],
  'en-US': [
    { id: 'en-US-Standard-C', name: 'Standard C (Female) — Free', gender: 'female', tier: 'Standard', locale: 'en-US' },
    { id: 'en-US-Standard-E', name: 'Standard E (Female) — Free', gender: 'female', tier: 'Standard', locale: 'en-US' },
    { id: 'en-US-Standard-A', name: 'Standard A (Male) — Free', gender: 'male', tier: 'Standard', locale: 'en-US' },
    { id: 'en-US-Standard-B', name: 'Standard B (Male) — Free', gender: 'male', tier: 'Standard', locale: 'en-US' },
    { id: 'en-US-Wavenet-C', name: 'WaveNet C (Female) — HD', gender: 'female', tier: 'WaveNet', locale: 'en-US' },
    { id: 'en-US-Wavenet-A', name: 'WaveNet A (Male) — HD', gender: 'male', tier: 'WaveNet', locale: 'en-US' },
    { id: 'en-US-Neural2-C', name: 'Neural2 C (Female) — Studio', gender: 'female', tier: 'Neural2', locale: 'en-US' },
    { id: 'en-US-Neural2-A', name: 'Neural2 A (Male) — Studio', gender: 'male', tier: 'Neural2', locale: 'en-US' },
  ],
  'en-GB': [
    { id: 'en-GB-Standard-A', name: 'Standard A (Female) — Free', gender: 'female', tier: 'Standard', locale: 'en-GB' },
    { id: 'en-GB-Standard-B', name: 'Standard B (Male) — Free', gender: 'male', tier: 'Standard', locale: 'en-GB' },
    { id: 'en-GB-Wavenet-A', name: 'WaveNet A (Female) — HD', gender: 'female', tier: 'WaveNet', locale: 'en-GB' },
    { id: 'en-GB-Wavenet-B', name: 'WaveNet B (Male) — HD', gender: 'male', tier: 'WaveNet', locale: 'en-GB' },
    { id: 'en-GB-Neural2-A', name: 'Neural2 A (Female) — Studio', gender: 'female', tier: 'Neural2', locale: 'en-GB' },
    { id: 'en-GB-Neural2-B', name: 'Neural2 B (Male) — Studio', gender: 'male', tier: 'Neural2', locale: 'en-GB' },
  ],
};

/**
 * Filter Google TTS voices by country and optional gender
 */
export function getGoogleVoicesForCountry(countryCode?: string, gender?: string): GoogleTTSVoice[] {
  const locale = getPrimaryLocaleForCountry(countryCode);
  const catalog = GOOGLE_TTS_VOICES[locale] || GOOGLE_TTS_VOICES['en-IN'] || [];
  if (!gender) return catalog;
  return catalog.filter(v => v.gender === gender);
}

/**
 * Shared gender detectors for installed/browser voice names.
 * They include the Azure/Google Neural speaker names (Ryan, Jenny, Sonia, Prabhat,
 * ...) so a male persona is never matched to a female voice (and vice versa).
 */
const SHARED_FEMALE_VOICE_REGEX = /female|woman|girl|samantha|zira|karen|ananya|priya|victoria|moira|tessa|veena|lekha|sangeeta|fiona|alice|allison|ava|susan|vicki|hazel|catherine|linda|heather|stephanie|julie|kate|serena|jenny|sonia|natasha|neerja|swara|kalpana|pallavi|clara|katja|aria|monica|michelle|emily/i;
const SHARED_MALE_VOICE_REGEX = /male|man|boy|david|alex|george|daniel|aravind|rahul|rishi|fred|oliver|tom|bruce|ralph|junior|albert|aaron|ryan|guy|william|liam|conrad|prabhat|madhur|james|henry|davis|brandon/i;

/**
 * Derives the gender encoded in an installed or cloud voice name/ID.
 * Returns null when the voice is genuinely neutral or unknown.
 */
export function inferGenderFromVoiceName(voiceName?: string): 'female' | 'male' | null {
  const raw = (voiceName || '').toLowerCase().trim();
  if (!raw) return null;
  const knownCloudVoice = OPENAI_TTS_VOICES.find((v) => v.id === raw);
  if (knownCloudVoice) return knownCloudVoice.gender === 'neutral' ? null : knownCloudVoice.gender;

  // Google Cloud Voice Pattern: e.g. en-IN-Standard-A or en-US-Wavenet-B
  const googleMatch = /^[a-z]{2}-[A-Z]{2}-(Standard|Wavenet|Neural2|Chirp|Studio)-([A-Z])$/i.exec(voiceName || '');
  if (googleMatch) {
    const letter = googleMatch[2].toUpperCase();
    if (['A', 'D', 'E', 'F'].includes(letter)) return 'female';
    if (['B', 'C', 'G', 'I', 'J'].includes(letter)) return 'male';
  }

  const explicitFemale = /\bfemale\b|\bwoman\b/.test(raw);
  const explicitMale = /\bmale\b|\bman\b/.test(raw) && !explicitFemale;
  if (explicitFemale || (!explicitMale && SHARED_FEMALE_VOICE_REGEX.test(raw))) return 'female';
  if (explicitMale || SHARED_MALE_VOICE_REGEX.test(raw)) return 'male';
  return null;
}

/**
 * Primary TTS locale for a country code (e.g. IN -> en-IN, GB -> en-GB, US -> en-US).
 */
export function getPrimaryLocaleForCountry(countryCode?: string): string {
  const preset = COUNTRY_PRESETS.find(c => c.code.toUpperCase() === (countryCode || 'IN').toUpperCase());
  return preset?.voiceLocale || preset?.locales?.[0] || 'en-IN';
}

/**
 * Filter available browser voices by country and optional gender
 */
export function getVoicesForCountry(countryCode?: string, gender?: string): SpeechSynthesisVoice[] {
  const allVoices = getCachedVoices();
  if (allVoices.length === 0) return [];

  const preset = COUNTRY_PRESETS.find(c => c.code.toUpperCase() === (countryCode || 'IN').toUpperCase()) || COUNTRY_PRESETS[0];
  const targetLocales = preset.locales.map(l => l.toLowerCase());

  // First pass: voices matching target country locales
  let matched = allVoices.filter(v => {
    const vLang = (v.lang || '').toLowerCase();
    return targetLocales.some(loc => vLang.startsWith(loc) || vLang.replace('_', '-').startsWith(loc));
  });

  // Fallback: if no voices match specific country, match English voices or all voices
  if (matched.length === 0) {
    matched = allVoices.filter(v => (v.lang || '').toLowerCase().startsWith('en'));
  }
  if (matched.length === 0) {
    matched = [...allVoices];
  }

  // Filter by gender if specified
  if (gender && gender !== 'neutral' && gender !== 'all') {
    const isFemale = gender.toLowerCase() === 'female';
    const femaleRegex = SHARED_FEMALE_VOICE_REGEX;
    const maleRegex = SHARED_MALE_VOICE_REGEX;

    // Prioritize explicit matches first
    const explicitMatches = matched.filter(v => {
      const name = v.name.toLowerCase();
      return isFemale
        ? (femaleRegex.test(name) || name.includes('female')) && !name.includes('male')
        : (maleRegex.test(name) || name.includes('male')) && !name.includes('female');
    });

    if (explicitMatches.length > 0) {
      return explicitMatches;
    }

    // Secondary filter: exclude opposite gender
    const filtered = matched.filter(v => {
      const name = v.name.toLowerCase();
      if (isFemale) {
        return !maleRegex.test(name) && !name.includes('male');
      } else {
        return !femaleRegex.test(name) && !name.includes('female');
      }
    });

    if (filtered.length > 0) {
      return filtered;
    }
  }

  return matched;
}

/**
 * Determine if a name suggests a female voice (supports Indian, Western, and global names)
 */
export const LOCKED_PERSONA_VOICE_STORAGE_PREFIX = 'trainova_locked_persona_voice_';

export function clearPersonaVoiceCache(personaName?: string): void {
  if (typeof window !== 'undefined') {
    try {
      if (personaName) {
        const raw = (personaName || '').toLowerCase().trim();
        const cleaned = (personaName || '').replace(/\s*\([^)]*\)/g, '').toLowerCase().trim();
        sessionStorage.removeItem(`${LOCKED_PERSONA_VOICE_STORAGE_PREFIX}${raw}`);
        if (cleaned && cleaned !== raw) {
          sessionStorage.removeItem(`${LOCKED_PERSONA_VOICE_STORAGE_PREFIX}${cleaned}`);
        }
      } else {
        Object.keys(sessionStorage).forEach((k) => {
          if (k.startsWith(LOCKED_PERSONA_VOICE_STORAGE_PREFIX)) {
            sessionStorage.removeItem(k);
          }
        });
      }
      sessionStorage.removeItem(`${LOCKED_PERSONA_VOICE_STORAGE_PREFIX}active`);
    } catch {}
  }
  roleVoiceCache.clear();
}

export function lockPersonaVoice(
  personaName: string | undefined,
  profile: {
    voiceName: string;
    voiceURI?: string;
    pitch: number;
    rate: number;
    lang?: string;
    gender?: string;
    ttsVoice?: string;
    countryCode?: string;
  }
): void {
  if (typeof window === 'undefined' || !profile) return;
  try {
    const rawName = (personaName || 'default').toLowerCase().trim();
    const cleanName = (personaName || 'default').replace(/\s*\([^)]*\)/g, '').toLowerCase().trim() || 'default';
    const json = JSON.stringify(profile);
    sessionStorage.setItem(`${LOCKED_PERSONA_VOICE_STORAGE_PREFIX}${cleanName}`, json);
    if (rawName !== cleanName) {
      sessionStorage.setItem(`${LOCKED_PERSONA_VOICE_STORAGE_PREFIX}${rawName}`, json);
    }
  } catch {}
}

export function getLockedPersonaVoice(
  personaName?: string,
  genderPreference?: string,
  ttsVoice?: string,
  countryCode?: string
): {
  voiceName: string;
  voiceURI?: string;
  pitch: number;
  rate: number;
  lang?: string;
  gender?: string;
  ttsVoice?: string;
  countryCode?: string;
} | null {
  if (typeof window === 'undefined') return null;
  try {
    const cleanName = (personaName || 'default').replace(/\s*\([^)]*\)/g, '').toLowerCase().trim() || 'default';
    const rawName = (personaName || 'default').toLowerCase().trim();
    const key = `${LOCKED_PERSONA_VOICE_STORAGE_PREFIX}${cleanName}`;
    let raw = sessionStorage.getItem(key);
    if (!raw && rawName !== cleanName) {
      raw = sessionStorage.getItem(`${LOCKED_PERSONA_VOICE_STORAGE_PREFIX}${rawName}`);
    }
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.voiceName) return null;

    // Invalidate if countryCode was specified and does not match the cached voice's country
    if (countryCode && parsed.countryCode && parsed.countryCode.toUpperCase() !== countryCode.toUpperCase()) {
      return null;
    }

    const vName = (parsed.voiceName || '').toLowerCase();
    const femaleRegex = SHARED_FEMALE_VOICE_REGEX;
    const maleRegex = SHARED_MALE_VOICE_REGEX;

    // Validate that the locked voice is compatible with current genderPreference if specified
    if (genderPreference && genderPreference !== 'neutral') {
      const isFemale = genderPreference === 'female';
      if (isFemale && maleRegex.test(vName) && !femaleRegex.test(vName)) {
        return null; // Stale male voice locked for female persona
      }
      if (!isFemale && femaleRegex.test(vName) && !maleRegex.test(vName)) {
        return null; // Stale female voice locked for male persona
      }
    }

    // Validate that the locked voice matches the explicit ttsVoice if provided
    if (ttsVoice && ttsVoice.trim()) {
      const target = ttsVoice.toLowerCase().trim();
      if (parsed.ttsVoice && parsed.ttsVoice.toLowerCase().trim() !== target) {
        return null; // Stale cached voice for different TTS voice ID
      }
      if (['onyx', 'echo', 'fable'].includes(target) && femaleRegex.test(vName)) return null;
      if (['nova', 'shimmer'].includes(target) && maleRegex.test(vName)) return null;
      if (!['onyx', 'echo', 'nova', 'shimmer', 'alloy', 'fable'].includes(target)) {
        if (!vName.includes(target) && target !== (parsed.voiceURI || '').toLowerCase()) {
          return null; // User explicitly selected a different browser voice
        }
      }
    }

    return parsed;
  } catch {
    return null;
  }
}

/**
 * Determine if a name suggests a female voice (supports Indian, Western, and global names)
 */
export function isFemaleName(name?: string): boolean {
  if (!name || !name.trim()) return false; // Default to neutral/male if unspecified
  const lower = name.toLowerCase().trim();
  
  // Specific male names check first to avoid false substrings (includes Prakash Mehta, Rahul, etc.)
  const explicitMaleNames = [
    'prakash', 'rohan', 'rahul', 'amit', 'rajesh', 'suresh', 'ramesh', 'vikram', 'ajay',
    'sanjay', 'sunil', 'anil', 'arjun', 'vivek', 'aditya', 'deepak', 'rohit',
    'manoj', 'suraj', 'rishi', 'daniel', 'alex', 'aaron', 'david', 'michael',
    'james', 'john', 'robert', 'william', 'richard', 'thomas', 'charles',
    'george', 'oliver', 'fred', 'mr', 'mr.', 'sir', 'pradeep', 'karan', 'tarun',
    'varun', 'kunal', 'gaurav', 'ashok', 'vijay', 'krishna', 'mohan', 'alok',
    'mehta'
  ];

  const words = lower.split(/[\s,.-]+/);
  if (words.some((w) => explicitMaleNames.includes(w))) {
    return false;
  }

  const femalePatterns = [
    'sulochana', 'priya', 'anita', 'sita', 'gita', 'pooja', 'neha', 'kavita',
    'samantha', 'sarah', 'emily', 'jessica', 'karen', 'mary', 'lisa', 'anna',
    'deepa', 'meera', 'sunita', 'radha', 'mrs', 'ms', 'miss',
    'ritu', 'ananya', 'swati', 'shweta', 'divya', 'sneha', 'victoria', 'zira', 'veena'
  ];
  return femalePatterns.some((pattern) => lower.includes(pattern));
}

/**
 * Get the exact best matching voice for a specific role, ensuring both parties have distinct voices
 */
export function getBestVoiceForRole(
  role: 'persona' | 'learner' | 'coach',
  personaName?: string,
  lang: string = 'en',
  learnerName?: string,
  countryCode?: string,
  genderPreference?: string,
  tone?: string,
  ttsVoice?: string
): { voice: SpeechSynthesisVoice | null; pitch: number; rate: number } {
  const effectiveGender = genderPreference || (role === 'persona' ? (isFemaleName(personaName) ? 'female' : 'male') : (isFemaleName(learnerName) ? 'female' : 'male'));

  // Check if a persona voice is already locked for this persona session and matches target country
  if (role === 'persona') {
    const locked = getLockedPersonaVoice(personaName, effectiveGender, ttsVoice, countryCode);
    if (locked) {
      const allVoices = getCachedVoices();
      const matched = allVoices.find(v => v.voiceURI === locked.voiceURI || v.name === locked.voiceName);
      if (matched) {
        return {
          voice: matched,
          pitch: locked.pitch,
          rate: locked.rate,
        };
      }
    }
  }

  const cacheKey = `${role}_${personaName || ''}_${lang}_${countryCode || ''}_${effectiveGender}_${tone || ''}_${ttsVoice || ''}_${learnerName || ''}`;
  const existing = roleVoiceCache.get(cacheKey);
  if (existing && existing.voice) {
    return existing;
  }

  // Country cadence & pitch factors
  const countryPreset = countryCode
    ? (COUNTRY_PRESETS.find(c => c.code.toUpperCase() === countryCode.toUpperCase()) || null)
    : null;
  const countryCadenceRate = countryPreset ? countryPreset.cadenceRate : 1.0;
  const countryCadencePitch = countryPreset ? countryPreset.cadencePitch : 1.0;

  // Check Tone preset for base pitch & rate adjustments
  const tonePreset = tone ? TONE_PRESETS.find(t => t.id === tone.toLowerCase() || t.name.toLowerCase() === tone.toLowerCase()) : null;
  const baseToneRate = tonePreset ? tonePreset.defaultRate : 1.0;
  const baseTonePitch = tonePreset ? tonePreset.defaultPitch : 1.0;

  const isPersonaFemale = effectiveGender === 'female';
  const isLearnerFemale = learnerName ? isFemaleName(learnerName) : false;
  const voices = getCachedVoices();
  const isEn = lang.startsWith('en');
  const isHi = lang.startsWith('hi');

  let resolved: { voice: SpeechSynthesisVoice | null; pitch: number; rate: number };

  // 1. PERSONA (AI CUSTOMER) VOICE
  if (role === 'persona') {
    const normalizedVoice = (ttsVoice || '').toLowerCase().trim();

    // Check if user explicitly selected a direct installed browser voice
    const explicitBrowserVoice = normalizedVoice && !['onyx', 'echo', 'nova', 'shimmer', 'alloy', 'fable'].includes(normalizedVoice) && voices.length > 0
      ? voices.find(v => v.name.toLowerCase() === normalizedVoice || v.voiceURI === ttsVoice || v.name.toLowerCase().includes(normalizedVoice))
      : null;

    if (explicitBrowserVoice) {
      const defaultPitch = isPersonaFemale ? 1.02 : 0.92;
      resolved = {
        voice: explicitBrowserVoice,
        pitch: +(defaultPitch * baseTonePitch * countryCadencePitch).toFixed(2),
        rate: +(0.98 * baseToneRate * countryCadenceRate).toFixed(2)
      };
    } else {
      // Find voices matching the specified country and gender
      const countryVoices = countryCode ? getVoicesForCountry(countryCode, effectiveGender) : [];
      // Identify if there are true regional locale matches for this country (e.g. en-IN, en-GB, en-AU, etc.)
      const exactCountryMatches = countryPreset && countryVoices.length > 0
        ? countryVoices.filter(v => {
            const vLang = (v.lang || '').toLowerCase().replace('_', '-');
            return countryPreset.locales.some(loc => vLang.startsWith(loc.toLowerCase()));
          })
        : [];

      // Calibrate neural voice timbre:
      // onyx: deep baritone (0.88), echo: consultative male (0.94)
      // shimmer: bright female (1.08), nova: warm female (1.04)
      // fable: nuanced (0.98), alloy: balanced (0.98)
      let neuralPitchFactor = isPersonaFemale ? 1.02 : 0.92;
      let neuralRateFactor = 0.98;

      if (normalizedVoice === 'onyx') {
        neuralPitchFactor = 0.88;
        neuralRateFactor = 0.96;
      } else if (normalizedVoice === 'echo') {
        neuralPitchFactor = 0.94;
        neuralRateFactor = 0.97;
      } else if (normalizedVoice === 'shimmer') {
        neuralPitchFactor = 1.08;
        neuralRateFactor = 1.01;
      } else if (normalizedVoice === 'nova') {
        neuralPitchFactor = 1.04;
        neuralRateFactor = 0.99;
      } else if (normalizedVoice === 'fable' || normalizedVoice === 'alloy') {
        neuralPitchFactor = 0.98;
        neuralRateFactor = 0.98;
      }

      // Voice selection hierarchy with strict gender enforcement:
      let chosenVoice: SpeechSynthesisVoice | null = null;

      const femaleRegex = SHARED_FEMALE_VOICE_REGEX;
      const maleRegex = SHARED_MALE_VOICE_REGEX;

      // Prioritize explicit gender matches
      const explicitExact = exactCountryMatches.filter(v => {
        const name = v.name.toLowerCase();
        return isPersonaFemale
          ? (femaleRegex.test(name) || name.includes('female')) && !name.includes('male')
          : (maleRegex.test(name) || name.includes('male')) && !femaleRegex.test(name);
      });
      const explicitCountry = countryVoices.filter(v => {
        const name = v.name.toLowerCase();
        return isPersonaFemale
          ? (femaleRegex.test(name) || name.includes('female')) && !name.includes('male')
          : (maleRegex.test(name) || name.includes('male')) && !femaleRegex.test(name);
      });

      // Secondary filter: exclude opposite gender
      const genderExactMatches = exactCountryMatches.filter(v => {
        const name = v.name.toLowerCase();
        return isPersonaFemale
          ? (!maleRegex.test(name) || femaleRegex.test(name))
          : (!femaleRegex.test(name) || maleRegex.test(name));
      });

      const genderCountryMatches = countryVoices.filter(v => {
        const name = v.name.toLowerCase();
        return isPersonaFemale
          ? (!maleRegex.test(name) || femaleRegex.test(name))
          : (!femaleRegex.test(name) || maleRegex.test(name));
      });

      if (isHi) {
        // Priority for Hindi language:
        chosenVoice = isPersonaFemale
          ? voices.find(v => v.lang.startsWith('hi') && (v.name.includes('Kalpana') || v.name.includes('Swara') || v.name.toLowerCase().includes('female'))) || voices.find(v => v.lang.startsWith('hi')) || null
          : voices.find(v => v.lang.startsWith('hi') && (v.name.includes('Hemant') || v.name.includes('Madhur') || v.name.toLowerCase().includes('male'))) || voices.find(v => v.lang.startsWith('hi')) || null;
      } else if (explicitExact.length > 0) {
        // Explicitly matched native voice of correct gender for country
        chosenVoice = explicitExact[0];
      } else if (genderExactMatches.length > 0) {
        chosenVoice = genderExactMatches[0];
      } else if (explicitCountry.length > 0) {
        chosenVoice = explicitCountry[0];
      } else if (genderCountryMatches.length > 0) {
        chosenVoice = genderCountryMatches[0];
      }

      // Fallback voice selection by gender if no country voice matched
      if (!chosenVoice) {
        if (isPersonaFemale) {
          chosenVoice =
            voices.find((v) => (femaleRegex.test(v.name) || v.name.toLowerCase().includes('female')) && !v.name.toLowerCase().includes('male') && (isEn ? v.lang.startsWith('en') : v.lang.startsWith(lang))) ||
            voices.find((v) => !maleRegex.test(v.name) && !v.name.toLowerCase().includes('male') && (isEn ? v.lang.startsWith('en') : v.lang.startsWith(lang))) ||
            voices.find((v) => femaleRegex.test(v.name)) ||
            voices[0] || null;
        } else {
          chosenVoice =
            voices.find((v) => (maleRegex.test(v.name) || v.name.toLowerCase().includes('male')) && !femaleRegex.test(v.name) && (isEn ? v.lang.startsWith('en') : v.lang.startsWith(lang))) ||
            voices.find((v) => !femaleRegex.test(v.name) && !v.name.toLowerCase().includes('female') && (isEn ? v.lang.startsWith('en') : v.lang.startsWith(lang))) ||
            voices.find((v) => maleRegex.test(v.name)) ||
            voices[0] || null;
        }
      }

      // Safeguard: If the selected voice gender does not match the persona gender, adapt pitch so it sounds natural
      let genderCompensationPitch = 1.0;
      if (chosenVoice) {
        const vName = chosenVoice.name.toLowerCase();
        const hasMaleName = maleRegex.test(vName) || vName.includes('male');
        const hasFemaleName = femaleRegex.test(vName) || vName.includes('female');
        if (isPersonaFemale && hasMaleName && !hasFemaleName) {
          genderCompensationPitch = 1.25; // Pitch up male voice if system had to use it
        } else if (!isPersonaFemale && hasFemaleName && !hasMaleName) {
          genderCompensationPitch = 0.80; // Pitch down female voice to sound distinctively male
        }
      }

      // Compute final combined pitch and cadence rate:
      // Emotion Tone * Neural Voice Profile * Country Cadence * Gender Compensation
      const finalPitch = +(neuralPitchFactor * baseTonePitch * countryCadencePitch * genderCompensationPitch).toFixed(2);
      const finalRate = +(neuralRateFactor * baseToneRate * countryCadenceRate).toFixed(2);

      resolved = {
        voice: chosenVoice,
        pitch: Math.max(0.6, Math.min(1.8, finalPitch)),
        rate: Math.max(0.6, Math.min(1.8, finalRate)),
      };
    }

    // Lock the chosen persona voice with countryCode to ensure mid-session stability and cross-country freshness
    if (resolved.voice) {
      lockPersonaVoice(personaName, {
        voiceName: resolved.voice.name,
        voiceURI: resolved.voice.voiceURI,
        pitch: resolved.pitch,
        rate: resolved.rate,
        lang: resolved.voice.lang,
        gender: effectiveGender,
        ttsVoice: ttsVoice || resolved.voice.name,
        countryCode: countryCode ? countryCode.toUpperCase() : undefined,
      });
      roleVoiceCache.set(cacheKey, resolved);
    }
  } else if (role === 'coach') {
    // 2. COACH (MENTOR / EVALUATOR) VOICE
    const voice =
      voices.find((v) => (v.name.includes('Oliver') || v.name.includes('Daniel') || v.name.includes('Google UK English Male') || v.name.includes('George')) && v.lang.startsWith('en')) ||
      voices.find((v) => v.name.toLowerCase().includes('male') && v.lang.startsWith('en')) ||
      voices.find((v) => v.lang.startsWith('en')) ||
      voices[0] ||
      null;
    resolved = { voice, pitch: 0.88, rate: 0.94 };
  } else {
    // 3. LEARNER VOICE (GUARANTEED DISTINCT FROM PERSONA)
    if (isLearnerFemale) {
      const voice =
        voices.find((v) => (v.name.includes('Victoria') || v.name.includes('Karen') || v.name.includes('Google US English Female') || v.name.includes('Zira')) && v.lang.startsWith('en')) ||
        voices.find((v) => (v.name.includes('Samantha') || v.name.includes('Veena')) && v.lang.startsWith('en')) ||
        voices.find((v) => isEn ? v.lang.startsWith('en') : v.lang.startsWith(lang)) ||
        voices[0] ||
        null;
      resolved = { voice, pitch: 1.12, rate: 1.04 };
    } else {
      const voice =
        voices.find((v) => (v.name.includes('Alex') || v.name.includes('Aaron') || v.name.includes('Google US English') || v.name.includes('Fred') || v.name.includes('Tom')) && v.lang.startsWith('en')) ||
        voices.find((v) => (v.name.includes('David') || v.name.includes('Daniel')) && v.lang.startsWith('en')) ||
        voices.find((v) => v.lang.startsWith('en')) ||
        voices[0] ||
        null;
      resolved = { voice, pitch: 1.10, rate: 1.03 };
    }
  }

  if (resolved.voice) {
    roleVoiceCache.set(cacheKey, resolved);
  }
  return resolved;
}

export interface SpeakOptions {
  text: string;
  role?: 'persona' | 'learner' | 'coach';
  personaName?: string;
  learnerName?: string;
  lang?: string;
  country?: string;
  tone?: string;
  ttsVoice?: string;
  genderPreference?: string;
  pitch?: number;
  rate?: number;
  rateMultiplier?: number;
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (err?: any) => void;
  onBoundary?: (charIndex: number) => void;
}

/**
 * Execute resilient speech synthesis with watchdog and heartbeat pump
 */
export function speakSpeechSynthesis(options: SpeakOptions): { cancel: () => void } {
  const {
    text,
    role = 'persona',
    personaName,
    learnerName,
    lang = 'en-US',
    country,
    tone,
    ttsVoice,
    genderPreference,
    pitch,
    rate,
    rateMultiplier = 1.0,
    onStart,
    onEnd,
    onError,
    onBoundary,
  } = options;

  if (typeof window === 'undefined' || !('speechSynthesis' in window) || !text || !text.trim()) {
    if (onEnd) onEnd();
    return { cancel: () => {} };
  }

  // Only cancel prior speech if there is something actually speaking or pending
  const wasSpeaking = typeof window !== 'undefined' && 'speechSynthesis' in window &&
    (window.speechSynthesis.speaking || window.speechSynthesis.pending);
  if (wasSpeaking) {
    stopAllSpeech();
  }

  // Chromium desktop 15-second speech freeze workaround (heartbeat pump)
  // Call pause/resume if an utterance has been continuously speaking for >10s to reset Chrome's speech timer.
  // ONLY run on desktop Chromium; on Android and mobile devices, calling pause/resume causes speech glitches,
  // stutter, or "interrupted" cancellations with Google Speech Services.
  const isMobileClient = typeof navigator !== 'undefined' && (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent || '') ||
    (typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1 && /Macintosh/i.test(navigator.userAgent || ''))
  );

  if (!heartbeatInterval && typeof window !== 'undefined' && 'speechSynthesis' in window && !isMobileClient) {
    heartbeatInterval = setInterval(() => {
      try {
        if (window.speechSynthesis && window.speechSynthesis.speaking) {
          const now = Date.now();
          const hasLongRunningUtterance = Array.from(activeUtterances).some((u: any) => {
            return u.__startedAt && (now - u.__startedAt > 10000);
          });
          if (hasLongRunningUtterance) {
            window.speechSynthesis.pause();
            window.speechSynthesis.resume();
          }
        }
      } catch {}
    }, 2500);
  }

  let hasEnded = false;
  let isCanceled = false;
  let hasStarted = false;
  let fallbackAttempted = false;
  let cancelRetryAttempted = false;
  let scheduledSpeakTimer: any = null;
  let currentActiveUtterance: SpeechSynthesisUtterance | null = null;

  const cleanupUtterance = (targetUtterance: SpeechSynthesisUtterance | null) => {
    if (!targetUtterance) return;
    const pendingWatchdog = watchdogTimeouts.get(targetUtterance);
    if (pendingWatchdog) {
      clearTimeout(pendingWatchdog);
      watchdogTimeouts.delete(targetUtterance);
    }
    activeUtterances.delete(targetUtterance);
    if (activeUtterances.size === 0 && heartbeatInterval) {
      clearInterval(heartbeatInterval);
      heartbeatInterval = null;
    }
    if (typeof window !== 'undefined') {
      (window as any).__trainova_activeUtterances = Array.from(activeUtterances);
    }
  };

  const finish = (isError = false, err?: any) => {
    if (hasEnded) return;
    hasEnded = true;
    if (scheduledSpeakTimer) {
      clearTimeout(scheduledSpeakTimer);
      scheduledSpeakTimer = null;
    }
    cleanupUtterance(currentActiveUtterance);
    if (isError) {
      if (onError) onError(err);
      else if (onEnd) onEnd();
    } else {
      if (onEnd) onEnd();
    }
  };

  const utterance = new SpeechSynthesisUtterance(text);
  currentActiveUtterance = utterance;
  activeUtterances.add(utterance); // Retain in memory to prevent GC
  if (typeof window !== 'undefined') {
    (window as any).__trainova_activeUtterances = Array.from(activeUtterances);
  }

  // Determine effective gender: explicit preference > known OpenAI voice gender > persona name heuristic
  const effectiveGender = genderPreference || (
    ttsVoice === 'onyx' || ttsVoice === 'echo' ? 'male' :
    ttsVoice === 'nova' || ttsVoice === 'shimmer' ? 'female' :
    undefined
  );

  const voiceConfig = getBestVoiceForRole(role, personaName, lang, learnerName, country, effectiveGender, tone, ttsVoice);
  let allVoices = getCachedVoices();
  if (allVoices.length === 0 && typeof window !== 'undefined' && window.speechSynthesis) {
    try {
      allVoices = window.speechSynthesis.getVoices() || [];
      if (allVoices.length > 0) {
        voicesCache = allVoices;
      }
    } catch {}
  }

  const isHi = lang.toLowerCase().startsWith('hi');
  const ttsVoiceIsHi = ttsVoice ? (ttsVoice.toLowerCase().includes('hi-') || ttsVoice.toLowerCase().includes('hindi')) : false;

  // If specific direct browser voice was selected by user, attempt exact match only if compatible with target language
  const isOpenAiProfile = ttsVoice ? ['onyx', 'echo', 'nova', 'shimmer', 'alloy', 'fable'].includes(ttsVoice.toLowerCase().trim()) : false;
  if (ttsVoice && !isOpenAiProfile && allVoices.length > 0 && (!isHi || ttsVoiceIsHi)) {
    const customVoice = allVoices.find(v => v.name === ttsVoice || v.voiceURI === ttsVoice || v.name.toLowerCase().includes(ttsVoice.toLowerCase()));
    if (customVoice) {
      utterance.voice = customVoice;
    } else if (voiceConfig.voice) {
      utterance.voice = voiceConfig.voice;
    }
  } else if (voiceConfig.voice) {
    utterance.voice = voiceConfig.voice;
  }

  const effectivePitch = typeof pitch === 'number' ? +(voiceConfig.pitch * pitch).toFixed(2) : voiceConfig.pitch;
  const effectiveRate = typeof rate === 'number' ? +(voiceConfig.rate * rate).toFixed(2) : voiceConfig.rate;

  utterance.pitch = Math.max(0.5, Math.min(2.0, effectivePitch));
  utterance.rate = Math.max(0.6, Math.min(2.0, effectiveRate * rateMultiplier));
  utterance.lang = isHi ? 'hi-IN' : (utterance.voice?.lang || voiceConfig.voice?.lang || lang);

  utterance.onstart = () => {
    hasStarted = true;
    (utterance as any).__startedAt = Date.now();
    if (onStart) onStart();
  };

  utterance.onboundary = (e: any) => {
    if (onBoundary && typeof e.charIndex === 'number') {
      onBoundary(e.charIndex);
    }
  };

  utterance.onend = () => {
    finish(false);
  };

  utterance.onerror = (e) => {
    if (isCanceled) {
      cleanupUtterance(utterance);
      return;
    }

    // Flag browser autoplay/interaction policy rejection so consumer can prompt learner
    if (e.error === 'not-allowed') {
      (e as any).isAutoplayBlocked = true;
      cleanupUtterance(utterance);
      finish(true, e);
      return;
    }

    // Chromium cancel-collision before speech actually began
    if ((e.error === 'canceled' || e.error === 'interrupted') && !hasStarted && !isCanceled) {
      cleanupUtterance(utterance);
      if (!cancelRetryAttempted) {
        cancelRetryAttempted = true;
        console.warn('[Trainova VoiceService] Speech canceled before start (Chromium IPC collision). Retrying once with clean delay.');
        scheduledSpeakTimer = setTimeout(() => {
          if (hasEnded || isCanceled) return;
          try {
            if (window.speechSynthesis.paused) {
              window.speechSynthesis.resume();
            }
            const retryUtterance = new SpeechSynthesisUtterance(text);
            retryUtterance.voice = utterance.voice;
            retryUtterance.pitch = utterance.pitch;
            retryUtterance.rate = utterance.rate;
            retryUtterance.lang = utterance.lang;
            retryUtterance.onstart = utterance.onstart;
            retryUtterance.onboundary = utterance.onboundary;
            retryUtterance.onend = utterance.onend;
            retryUtterance.onerror = (reErr) => {
              cleanupUtterance(retryUtterance);
              if (reErr.error === 'canceled' || reErr.error === 'interrupted') {
                finish(true, reErr);
                return;
              }
              finish(true, reErr);
            };
            currentActiveUtterance = retryUtterance;
            activeUtterances.add(retryUtterance);
            window.speechSynthesis.speak(retryUtterance);
          } catch (reErr) {
            finish(true, reErr);
          }
        }, 80);
        return;
      }
      finish(true, e);
      return;
    }

    if (e.error === 'canceled' || e.error === 'interrupted') {
      cleanupUtterance(utterance);
      return;
    }

    // Automatic fallback to system default voice if cloud/remote voice fails
    if (
      !fallbackAttempted &&
      (e.error === 'network' || e.error === 'synthesis-failed' || e.error === 'audio-busy' || e.error === 'language-unavailable' || e.error === 'voice-unavailable')
    ) {
      fallbackAttempted = true;
      console.warn(`[Trainova VoiceService] Voice error (${e.error}). Retrying with default local system voice.`);
      cleanupUtterance(utterance);
      try {
        if (window.speechSynthesis.paused) {
          window.speechSynthesis.resume();
        }
        const fallbackUtterance = new SpeechSynthesisUtterance(text);
        currentActiveUtterance = fallbackUtterance;
        activeUtterances.add(fallbackUtterance);

        // Find a local service voice if available, or use null for browser default
        const localVoice = allVoices.find(v => v.localService && (isHi ? v.lang.startsWith('hi') : v.lang.startsWith('en'))) || null;
        fallbackUtterance.voice = localVoice;
        fallbackUtterance.pitch = utterance.pitch;
        fallbackUtterance.rate = utterance.rate;
        fallbackUtterance.lang = utterance.lang;

        fallbackUtterance.onstart = () => {
          hasStarted = true;
          (fallbackUtterance as any).__startedAt = Date.now();
          if (onStart) onStart();
        };
        fallbackUtterance.onboundary = utterance.onboundary;
        fallbackUtterance.onend = () => {
          cleanupUtterance(fallbackUtterance);
          finish(false);
        };
        fallbackUtterance.onerror = (fErr) => {
          cleanupUtterance(fallbackUtterance);
          if (fErr.error === 'canceled' || fErr.error === 'interrupted') return;
          finish(true, fErr);
        };

        window.speechSynthesis.speak(fallbackUtterance);
        return;
      } catch (fallbackErr) {
        console.warn('[Trainova VoiceService] Local fallback dispatch note:', fallbackErr);
      }
    }

    finish(true, e);
  };

  // Generous safety watchdog: allows natural conversational pacing and prevents early sentence cutoff
  const wordCount = text.split(/\s+/).filter(Boolean).length;
  const estimatedMs = Math.max(12000, ((wordCount / (0.9 * rateMultiplier)) * 1000) + 15000);
  const watchdogHandle = setTimeout(() => {
    watchdogTimeouts.delete(utterance);
    if (!hasEnded && activeUtterances.has(utterance)) {
      console.warn('[Trainova VoiceService] Speech watchdog timeout triggered for:', text.slice(0, 30));
      finish(false);
    }
  }, estimatedMs);
  watchdogTimeouts.set(utterance, watchdogHandle);

  // Settling delay: allows Chromium speech synthesis IPC cancellation to settle to avoid cancel collision
  const startDelay = wasSpeaking ? 80 : 20;
  scheduledSpeakTimer = setTimeout(() => {
    if (hasEnded || isCanceled) return;
    try {
      if (window.speechSynthesis.paused) {
        window.speechSynthesis.resume();
      }
      window.speechSynthesis.speak(utterance);
    } catch (err) {
      console.warn('[Trainova VoiceService] Speech synthesis call error:', err);
      finish(true, err);
    }
  }, startDelay);

  return {
    cancel: () => {
      isCanceled = true;
      if (scheduledSpeakTimer) {
        clearTimeout(scheduledSpeakTimer);
        scheduledSpeakTimer = null;
      }
      finish(false);
      try {
        window.speechSynthesis.cancel();
      } catch {}
    },
  };
}

export function stopAllSpeech(): void {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
  for (const handle of watchdogTimeouts.values()) {
    clearTimeout(handle);
  }
  watchdogTimeouts.clear();
  activeUtterances.clear();
  try {
    if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
      window.speechSynthesis.cancel();
    }
  } catch {}
}

export function pauseSpeech(): void {
  if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
    try {
      if (window.speechSynthesis.speaking) {
        window.speechSynthesis.pause();
      }
    } catch {}
  }
}

export function resumeSpeech(): void {
  if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
    try {
      if (window.speechSynthesis.paused) {
        window.speechSynthesis.resume();
      }
    } catch {}
  }
}
